import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AGENCIA_PORTES } from "@/types/agencia";
import { reportUserError } from "@/lib/report-error";

interface AgenciaModalProps {
  onSuccess: () => void;
}

const PORTES_OPTIONS = AGENCIA_PORTES.map((porte) => ({
  label: porte,
  value: porte,
}));

export function AgenciaModal({ onSuccess }: AgenciaModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [nome, setNome] = useState("");
  const [segmento, setSegmento] = useState("");
  const [categorias, setCategorias] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [website, setWebsite] = useState("");
  const [cidade, setCidade] = useState("");
  const [bairro, setBairro] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [logoPath, setLogoPath] = useState("");
  const [cor, setCor] = useState("");
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { error } = await supabase
        .from("agencias")
        .insert([{
          nome,
          segmento,
          categorias: categorias.length > 0 ? categorias : null,
          email,
          telefone,
          website,
          cidade,
          bairro,
          observacoes,
          logo_path: logoPath || null,
          cor: cor || null
        }]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Agência cadastrada com sucesso!",
      });

      onSuccess();
      setIsOpen(false);
      setNome("");
      setSegmento("");
      setCategorias([]);
      setEmail("");
      setTelefone("");
      setWebsite("");
      setCidade("");
      setBairro("");
      setObservacoes("");
    } catch (error) {
      console.error("Erro ao criar agência:", error);
      toast({
        title: "Erro",
        description: "Não foi possível cadastrar a agência. " + reportUserError(error, { source: "agencia-modal" }).description,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Nova Agência
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Cadastrar Nova Agência</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome da Agência *</Label>
            <Input
              id="nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="segmento">Segmento</Label>
              <Input
                id="segmento"
                value={segmento}
                onChange={(e) => setSegmento(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Portes</Label>
              <MultiSelect
                options={PORTES_OPTIONS}
                selected={categorias}
                onChange={setCategorias}
                placeholder="Selecione..."
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              id="telefone"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bairro">Bairro</Label>
              <Input
                id="bairro"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logoPath">Logo (caminho do arquivo)</Label>
              <Input
                id="logoPath"
                value={logoPath}
                onChange={(e) => setLogoPath(e.target.value)}
                placeholder="Ex: /almap-logo.jpg.jpeg"
              />
              {logoPath && (
                <img
                  src={logoPath}
                  alt="Preview da logo"
                  className="h-10 max-w-[160px] object-contain mt-1"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cor">Cor da agência</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="cor"
                  value={cor || "#6d28d9"}
                  onChange={(e) => setCor(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-input bg-background p-1"
                />
                <Input
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                  placeholder="#6d28d9"
                  className="flex-1"
                />
              </div>
              {cor && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-6 w-6 rounded" style={{ backgroundColor: cor }} />
                  <span className="text-xs text-muted-foreground">{cor}</span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
            />
          </div>
          <Button type="submit" className="w-full">
            Cadastrar
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
